import { useState } from "react";
// import CheckBox from '@mui/icons-material/CheckBox';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useGetPaymentMethods, useAddPaySetupIntent } from '../../hooks/paymenthooks'
import config from '../../config';


var webUrl = config?.webUrl

type PaymentFormProps = {
  communityId?: string
  isAddPayMethod?: boolean
  uid: string
  setIsAddingPayMethod: React.Dispatch<React.SetStateAction<boolean>>
  clientSecret?: string
}

const PaymentForm: React.FC<PaymentFormProps> = ({isAddPayMethod, uid, communityId, setIsAddingPayMethod, clientSecret}) => {
  const { reloadPaymentMethods } = useGetPaymentMethods(uid)
  const { addSetupIntent, isLoadingSetupIntent } = useAddPaySetupIntent(uid, communityId)
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  // const [preferredMethod, setPreferredMethod] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);


  const handleSubmitPay = async () => {
    if (isProcessing) return; // Prevent double submission
    
    setError(null); // Clear any previous errors
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Use the clientSecret from props (provided by Elements initialization)
      // If not available, create a new one as fallback
      let setupClientSecret = clientSecret;
      if (!setupClientSecret) {
        setupClientSecret = await addSetupIntent();
        if (!setupClientSecret) {
          setError("Failed to create payment setup. Please try again.");
          setIsProcessing(false);
          return;
        }
      }

      // Now trigger form validation and wallet collection
      // Elements will use the setup intent we provided
      const {error: submitError} = await elements.submit();
      if (submitError?.message) {
        setError(submitError.message);
        setIsProcessing(false);
        return;
      }

      // Use the clientSecret and Elements instance to confirm the setup
      const {error: confirmError, setupIntent} = await stripe.confirmSetup({
        elements,
        clientSecret: setupClientSecret,
        confirmParams: {
          return_url: `${webUrl}/wallet`,
        },
        redirect: "if_required",
      });

      if (confirmError) {
        // Handle specific error types
        if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
          setError(confirmError.message || "Payment setup failed. Please check your card details and try again.");
        } else if (confirmError.code === 'setup_intent_unexpected_state') {
          setError("This payment setup has already been processed. Please refresh and try again.");
        } else {
          setError(confirmError.message || "An error occurred while processing your payment. Please try again.");
        }
        setIsProcessing(false);
        return;
      }

      // Success - reload payment methods and handle setup intent
      if (setupIntent && setupIntent.status === 'succeeded') {
        await reloadPaymentMethods()
        // Optionally close the form or show success message
      }
    } catch (err: any) {
      if (process.env.NODE_ENV !== 'test') {
        console.error("Error in handleSubmitPay:", err);
      }
      // Handle specific Stripe errors
      if (err?.code === 'resource_missing' || err?.message?.includes('No such setupintent')) {
        setError("The payment setup session has expired. Please try again.");
      } else {
        setError(err?.message || "An error occurred while processing your payment. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  };



  const handleAddPaymentMethod = async () => {
    if (isProcessing) return; // Prevent double submission
    
    setError(null); // Clear any previous errors
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsProcessing(true);

    try {
      // Use the clientSecret from props (provided by Elements initialization)
      // If not available, create a new one as fallback
      let setupClientSecret = clientSecret;
      if (!setupClientSecret) {
        setupClientSecret = await addSetupIntent();
        if (!setupClientSecret) {
          setError("Failed to create payment setup. Please try again.");
          setIsProcessing(false);
          return;
        }
      }

      // Now trigger form validation and wallet collection
      // Elements will use the setup intent we provided
      const {error: submitError} = await elements.submit();
      if (submitError?.message) {
        setError(submitError.message);
        setIsProcessing(false);
        return;
      }

      // Use the clientSecret and Elements instance to confirm the setup
      const {error: confirmError, setupIntent} = await stripe.confirmSetup({
        elements,
        clientSecret: setupClientSecret,
        confirmParams: {
          return_url: `${webUrl}/wallet`,
        },
        redirect: "if_required",
      });      

      if (confirmError) {
        // Handle specific error types
        if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
          setError(confirmError.message || "Payment setup failed. Please check your card details and try again.");
        } else if (confirmError.code === 'setup_intent_unexpected_state') {
          setError("This payment setup has already been processed. Please refresh and try again.");
        } else {
          setError(confirmError.message || "An error occurred while adding your payment method.");
        }
        setIsProcessing(false);
        return;
      }

      // Success - reload payment methods
      if (setupIntent && setupIntent.status === 'succeeded') {
        await reloadPaymentMethods()
        setIsAddingPayMethod(false)
      }

    } catch(err: any) {
      if (process.env.NODE_ENV !== 'test') {
        console.error("Error in handleAddPaymentMethod:", err);
      }
      // Handle specific Stripe errors
      if (err?.code === 'resource_missing' || err?.message?.includes('No such setupintent')) {
        setError("The payment setup session has expired. Please try again.");
      } else {
        setError(err?.message || "An error occurred while adding your payment method. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }

  };

  return (
    <div className='flex flex-col w-full'>
        <PaymentElement className="text-[14px] text-[#4D4D4D] font-raleway" />
        {/* {isAddPayMethod ? <span onClick={()=> setPreferredMethod(prev=> !prev)} className='w-full m-1 mt-6 text-[14px] font-raleway text-[#4D4D4D] cursor-pointer'>{preferredMethod ? (<CheckBox />): (<CheckBoxOutlineBlankIcon />)} Set as preferred payment method</span> : <></>} */}
        {
          (isAddPayMethod) ? (
            <button onClick={handleAddPaymentMethod} className="bg-primaryDark-0 hover:bg-primaryDark-300 ml-auto text-white font-raleway font-bold text-[14px] rounded-md border-0 py-[8px] px-[18px] w-[200px] my-4" disabled={!stripe || isProcessing || isLoadingSetupIntent}>
              {isProcessing || isLoadingSetupIntent ? "Processing..." : "Add Payment Method"}
            </button>
          ) : (
            <button onClick={handleSubmitPay} className={`buttonsPrimary border-0 mt-2 m-1 p-2 "order-1 !bg-primary-200 !text-back-400 text-sm`} disabled={!stripe || isProcessing || isLoadingSetupIntent}>
              {isProcessing || isLoadingSetupIntent ? "Processing..." : "Add Card"}
            </button>
          )
        }
        {error && <div className="flex w-full h-10 bg-red-100/90 text-red-800 text-xs font-open-sans rounded-sm p-1 m-1">{error}</div>}
        
    </div>
  )
};

export default PaymentForm;
